import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Input as InputIcon,
  Output as OutputIcon,
} from '@mui/icons-material';
import { WidgetProps } from '../types/index.js';
import { useCanvasStore } from '../stores/canvasStore.js';

interface GroupConfig {
  groupId: string;
  collapsed?: boolean;
}

export const WidgetGroupWidget: React.FC<WidgetProps> = ({
  widget,
}) => {
  const config = widget.config as GroupConfig;
  const zoomToGroup = useCanvasStore((state) => state.zoomToGroup);
  const activeCanvas = useCanvasStore((state) => state.getActiveCanvas());
  
  // Find the group data
  const group = activeCanvas?.groups?.find(g => g.id === config.groupId);
  
  if (!group) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Group not found</Typography>
      </Box>
    );
  }

  // Handle zoom to group (double-click)
  const handleZoomToGroup = () => {
    if (activeCanvas && config.groupId) {
      zoomToGroup(activeCanvas.id, config.groupId);
    }
  };

  // Attempt to surface run header info (status, cost, summary) on the group card
  const runHeader = group.widgets.find((w: any) => w.type === 'runHeader');
  const headerInputs = Object.fromEntries(
    (runHeader?.inputs || []).map((i: any) => [i.id, i.value])
  );
  const status: 'queued' | 'running' | 'complete' | 'error' = (group as any).status || headerInputs.status || 'running';
  const summary: string | undefined = headerInputs.summary;
  const prompt: string | undefined = headerInputs.prompt;
  const finalText: string | undefined = headerInputs.final;
  const cost: { estimatedUsd?: number } | undefined = headerInputs.cost;
  const finalData: any = headerInputs.finalData;
  const trace: Array<{ t: number; text: string; tool?: string }> = (headerInputs.trace || (group as any).trace || []) as any;

  // Widget groups are containers - their outputs are set when created
  // and updated through the data flow execution in the canvas store

  return (
    <Box 
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onDoubleClick={handleZoomToGroup}
    >
      {/* Header */}
      <Box sx={{ 
        p: 1.5, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'action.hover',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'action.selected',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            {group.name}
          </Typography>
          <Chip label={`${group.widgets.length} widgets`} size="small" variant="outlined" />
          <Chip
            label={status}
            size="small"
            color={status === 'complete' ? 'success' : status === 'error' ? 'error' : status === 'queued' ? 'default' : 'primary'}
          />
          {typeof cost?.estimatedUsd === 'number' && (
            <Chip size="small" variant="outlined" label={`$${cost!.estimatedUsd.toFixed(3)}`} />
          )}
        </Box>
      </Box>

      {/* Loading indicator for running state */}
      {status === 'running' && (
        <LinearProgress color="primary" sx={{ height: 2 }} />
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {/* Prompt */}
        {prompt && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Prompt
            </Typography>
            <Box sx={{
              mb: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: 'action.hover',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              <Typography variant="body2">{prompt}</Typography>
            </Box>
          </>
        )}

        {/* Summary (live one-liner) */}
        {summary && (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {summary}
          </Typography>
        )}

        {/* Compact KPI strip when we have structured final data */}
        {Array.isArray(finalData?.metrics) && finalData.metrics.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {finalData.metrics.slice(0, 4).map((m: any, idx: number) => (
              <Box key={idx} sx={{ p: 0.75, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.75 }}>{m.label}</Typography>
                <Typography variant="body2" component="span" fontWeight={600} sx={{ mr: 0.75 }}>{String(m.value)}</Typography>
                {typeof m.deltaPct === 'number' && (
                  <Chip
                    size="small"
                    label={`${m.deltaPct >= 0 ? '+' : ''}${m.deltaPct.toFixed(2)}%`}
                    color={m.deltaPct >= 0 ? 'success' : 'error'}
                    sx={{ height: 20 }}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}

        <List dense sx={{ p: 0 }}>
          {/* Show exposed inputs */}
          {group.exposedInputs.length > 0 && (
            <>
              <ListItem>
                <ListItemIcon>
                  <InputIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Inputs"
                  secondary={`${group.exposedInputs.length} exposed`}
                />
              </ListItem>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Show exposed outputs */}
          {group.exposedOutputs.length > 0 && (
            <>
              <ListItem>
                <ListItemIcon>
                  <OutputIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Outputs"
                  secondary={`${group.exposedOutputs.length} exposed`}
                />
              </ListItem>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Final response */}
          {finalText && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Final
              </Typography>
              <Box sx={{
                mb: 2,
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
                maxHeight: 140,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                <Typography variant="body2">{finalText}</Typography>
              </Box>
            </>
          )}

          {/* Recent step timeline (compact) */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Steps
          </Typography>
          {(trace || []).slice(-5).reverse().map((e, idx) => (
            <Tooltip key={idx} title={new Date(e.t).toLocaleTimeString()} placement="right">
              <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {e.tool ? `${e.tool}: ` : ''}{e.text}
              </Typography>
            </Tooltip>
          ))}
          {(!trace || trace.length === 0) && (
            <Typography variant="caption" color="text.disabled">Awaiting steps…</Typography>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Widget list */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Contains
          </Typography>
          {group.widgets.map((w) => (
            <ListItem key={w.id} dense>
              <ListItemText
                primary={w.config.title || w.type}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Footer */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 1, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Double-click to zoom in • Right-click to ungroup
        </Typography>
      </Paper>
    </Box>
  );
};
